import React, { useState } from 'react';
import { Phone, Mail, ArrowRight } from 'lucide-react';
import { contactInfo } from '../mock';
import { useToast } from '../hooks/use-toast';
import { useT } from '../i18n/LanguageContext';
import { createContact } from '../lib/api';

export default function ContactCTA() {
  const { toast } = useToast();
  const { t, lang } = useT();
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast({ title: t('contact.toast.missing'), description: t('contact.toast.missingDesc') });
      return;
    }
    setSubmitting(true);
    try {
      await createContact({ ...form, language: lang });
      toast({ title: t('contact.toast.sent'), description: t('contact.toast.sentDesc') });
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast({
        title: lang === 'pt' ? 'Erro no envio' : 'Submission error',
        description: lang === 'pt'
          ? 'Não foi possível enviar. Tente novamente ou fale conosco no WhatsApp.'
          : 'We couldn’t send your request. Please try again or reach us on WhatsApp.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const upd = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <section id="contact" className="relative py-24 bg-[#1a2851] overflow-hidden">
      <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-[#4dbfae]/10 blur-3xl" />
      <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-[#4dbfae]/10 blur-3xl" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
        <div className="text-white">
          <p className="uc-kicker text-[#7fe0ce] mb-3">{t('contact.kicker')}</p>
          <h2 className="font-serif text-4xl md:text-5xl font-bold leading-tight mb-6">
            {t('contact.heading1')} <span className="text-[#7fe0ce]">{t('contact.brand')}</span> {t('contact.heading2')}
          </h2>
          <p className="text-white/80 text-lg mb-8 max-w-md">{t('contact.sub')}</p>

          <div className="space-y-4">
            <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-full bg-white/10 group-hover:bg-[#4dbfae] transition-colors flex items-center justify-center">
                <Phone size={20} />
              </span>
              <div>
                <p className="text-xs uppercase tracking-widest text-white/60">{t('contact.callFor')}</p>
                <p className="text-xl font-semibold">{contactInfo.phone}</p>
              </div>
            </a>
            <a href={`mailto:${contactInfo.email}`} className="flex items-center gap-4 group">
              <span className="w-12 h-12 rounded-full bg-white/10 group-hover:bg-[#4dbfae] transition-colors flex items-center justify-center">
                <Mail size={20} />
              </span>
              <div>
                <p className="text-xs uppercase tracking-widest text-white/60">{t('contact.email')}</p>
                <p className="text-lg font-medium">{contactInfo.email}</p>
              </div>
            </a>
          </div>
        </div>

        <form onSubmit={onSubmit} className="bg-white rounded-2xl p-8 lg:p-10 shadow-2xl">
          <h3 className="font-serif text-2xl font-bold text-[#1a2851] mb-2">{t('contact.form.title')}</h3>
          <p className="text-sm text-[#5b6469] mb-6">
            {t('contact.form.sub')}
          </p>
          <div className="grid sm:grid-cols-2 gap-4">
            <input type="text" placeholder={t('contact.form.name')} value={form.name} onChange={upd('name')} className="w-full px-4 py-3 rounded-lg border border-[#dfeceb] focus:outline-none focus:border-[#4dbfae] focus:ring-2 focus:ring-[#4dbfae]/20 transition" />
            <input type="email" placeholder={t('contact.form.email')} value={form.email} onChange={upd('email')} className="w-full px-4 py-3 rounded-lg border border-[#dfeceb] focus:outline-none focus:border-[#4dbfae] focus:ring-2 focus:ring-[#4dbfae]/20 transition" />
          </div>
          <input type="tel" placeholder={t('contact.form.phone')} value={form.phone} onChange={upd('phone')} className="w-full mt-4 px-4 py-3 rounded-lg border border-[#dfeceb] focus:outline-none focus:border-[#4dbfae] focus:ring-2 focus:ring-[#4dbfae]/20 transition" />
          <textarea placeholder={t('contact.form.message')} value={form.message} onChange={upd('message')} rows={4} className="w-full mt-4 px-4 py-3 rounded-lg border border-[#dfeceb] focus:outline-none focus:border-[#4dbfae] focus:ring-2 focus:ring-[#4dbfae]/20 transition resize-none" />
          <button type="submit" disabled={submitting} className="uc-btn mt-6 w-full justify-center disabled:opacity-60">
            {submitting ? (lang === 'pt' ? 'Enviando…' : 'Sending…') : t('contact.form.send')}
            <ArrowRight size={18} />
          </button>
        </form>
      </div>
    </section>
  );
}
